import {
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EyeInvisibleOutlined,
  FullscreenOutlined,
  HolderOutlined,
  LockOutlined,
  MoreOutlined,
  ReloadOutlined,
  SettingOutlined,
  UpOutlined
} from "@ant-design/icons"
import {
  Button,
  Card,
  Dropdown,
  Flex,
  type MenuProps,
  Popconfirm,
  Skeleton
} from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../../components/ActionTooltip.tsx"
import { WidgetRenderer } from "../../../../components/widgets/WidgetRenderer.tsx"
import {
  brand,
  neutral,
  shadows,
  widgetActionGap
} from "../../../../components/widgets/tokens.ts"
import type {
  WidgetInstance,
  WidgetPayload,
  WidgetProps
} from "../../../../components/widgets/types.ts"

const cardStyle: React.CSSProperties = {
  border: `1px solid ${neutral.border}`,
  borderRadius: 12,
  boxShadow: shadows.card
}

const chartCardStyle: React.CSSProperties = {
  ...cardStyle,
  height: "100%",
  display: "flex",
  flexDirection: "column"
}

const chartCardBodyStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: "hidden"
}

const overlayBaseStyle: React.CSSProperties = {
  position: "absolute",
  top: 6,
  zIndex: 2
}

// Placeholder copy grows with the tile's width, so it is centred in a clipped
// body rather than left to spill over the tile beneath it on the canvas.
const placeholderCardStyle: React.CSSProperties = {
  ...cardStyle,
  height: "100%",
  display: "flex",
  flexDirection: "column"
}

const placeholderBodyStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
}

const actionButtonSize = 24
const actionEdgeInset = 6

type TileStyle = React.CSSProperties & {
  "--report-widget-actions-offset": string
}

// Widgets that build their own frame size to their content, so without a box to
// clip them a long table paints over the tiles beneath it on the canvas.
const frameWrapperStyle: React.CSSProperties = {
  height: "100%",
  overflow: "hidden"
}

export type ReportTileState =
  | "loading"
  | "ready"
  | "error"
  | "empty"
  | "unavailable"
  | "restricted"

interface ReportCanvasWidgetTileProps {
  widget: WidgetInstance
  payload: WidgetPayload | null
  state: ReportTileState
  currency?: string
  collapsed: boolean
  title: string
  readOnly?: boolean
  selectable?: boolean
  selected?: boolean
  onSelect?: () => void
  onConfigure: () => void
  onDuplicate: () => void
  onToggleCollapse: () => void
  onRemove: () => void
  onRefresh: () => void
  onExpand?: () => void
  onEngagementDrill?: WidgetProps["onEngagementDrill"]
  loadEngagementRange?: WidgetProps["loadEngagementRange"]
}

export const ReportCanvasWidgetTile = ({
  widget,
  payload,
  state,
  currency,
  collapsed,
  title,
  readOnly = false,
  selectable = false,
  selected = false,
  onSelect,
  onConfigure,
  onDuplicate,
  onToggleCollapse,
  onRemove,
  onRefresh,
  onExpand,
  onEngagementDrill,
  loadEngagementRange
}: ReportCanvasWidgetTileProps) => {
  const { t } = useTranslation()
  const canvasActionCount = readOnly ? 1 : 2
  const canvasActionsOffset =
    actionEdgeInset +
    canvasActionCount * actionButtonSize +
    canvasActionCount * widgetActionGap
  const tileStyle: TileStyle = {
    "--report-widget-actions-offset": `${canvasActionsOffset}px`,
    ...(selected
      ? {
          outline: `2px dashed ${brand.teal}`,
          outlineOffset: 2,
          borderRadius: 12
        }
      : {})
  }

  const refreshItem = {
    key: "refresh",
    icon: <ReloadOutlined />,
    label: t("reports.canvas.actions.refresh"),
    onClick: onRefresh
  }

  // Framed widgets carry the Full Screen affordance in their own chrome; chart
  // tiles have no frame, so they offer it through the actions menu instead.
  const fullscreenItems =
    widget.type === "chart" && onExpand && !collapsed
      ? [
          {
            key: "fullscreen",
            icon: <FullscreenOutlined />,
            label: t("widget.action.expand", "Expand"),
            onClick: onExpand
          }
        ]
      : []

  const menuItems: MenuProps["items"] = readOnly
    ? [refreshItem, ...fullscreenItems]
    : [
        {
          key: "configure",
          icon: <SettingOutlined />,
          label: t("reports.canvas.actions.configure"),
          onClick: onConfigure
        },
        {
          key: "duplicate",
          icon: <CopyOutlined />,
          label: t("reports.canvas.actions.duplicate"),
          onClick: onDuplicate
        },
        {
          key: "collapse",
          icon: collapsed ? <DownOutlined /> : <UpOutlined />,
          label: collapsed
            ? t("reports.canvas.actions.expand")
            : t("reports.canvas.actions.collapse"),
          onClick: onToggleCollapse
        },
        ...fullscreenItems
      ]

  return (
    <div
      className="reports-canvas-tile relative h-full"
      data-testid={`canvas-widget-${widget.id}`}
      data-selected={selected || undefined}
      style={tileStyle}
    >
      {selectable && (
        <div
          role="button"
          tabIndex={0}
          aria-label={t("reports.canvas.actions.configure")}
          aria-pressed={selected}
          onClick={onSelect}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onSelect?.()
            }
          }}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            cursor: "pointer",
            borderRadius: 12
          }}
        />
      )}
      {!readOnly && (
        <div
          className="rgl-drag-handle"
          role="img"
          aria-label={t("reports.canvas.actions.dragHandle")}
          style={{
            ...overlayBaseStyle,
            left: actionEdgeInset,
            cursor: "grab",
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 6,
            color: neutral.muted,
            background: "rgba(255,255,255,0.85)"
          }}
        >
          <HolderOutlined />
        </div>
      )}

      <div
        data-testid="report-widget-actions"
        style={{
          ...overlayBaseStyle,
          right: actionEdgeInset,
          display: "flex",
          gap: widgetActionGap
        }}
      >
        <ActionTooltip title={t("reports.canvas.actions.menu")}>
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button
              type="text"
              size="small"
              aria-label={t("reports.canvas.actions.menu")}
              icon={<MoreOutlined />}
            />
          </Dropdown>
        </ActionTooltip>
        {!readOnly && (
          <ActionTooltip title={t("reports.canvas.actions.remove")}>
            <Popconfirm
              title={t("reports.canvas.actions.removeConfirmTitle")}
              onConfirm={onRemove}
              okText={t("reports.canvas.actions.remove")}
              cancelText={t("reports.cancel")}
            >
              <Button
                type="text"
                size="small"
                danger
                aria-label={t("reports.canvas.actions.remove")}
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </ActionTooltip>
        )}
      </div>

      {collapsed ? (
        <Card
          size="small"
          title={title}
          style={{ ...cardStyle, height: "100%" }}
        />
      ) : state === "restricted" ? (
        <Card
          size="small"
          title={title}
          style={placeholderCardStyle}
          styles={{ body: placeholderBodyStyle }}
        >
          <Flex
            vertical
            align="center"
            gap={4}
            data-testid="widget-restricted"
            style={{ textAlign: "center" }}
          >
            <LockOutlined style={{ fontSize: 20, color: neutral.muted }} />
            <span style={{ fontWeight: 600 }}>
              {t("reports.canvas.widgetRestricted.title")}
            </span>
            <span style={{ color: neutral.muted }}>
              {t("reports.canvas.widgetRestricted.description")}
            </span>
          </Flex>
        </Card>
      ) : state === "unavailable" ? (
        <Card
          size="small"
          title={title}
          style={placeholderCardStyle}
          styles={{ body: placeholderBodyStyle }}
        >
          <Flex
            vertical
            align="center"
            gap={4}
            style={{ color: neutral.muted, textAlign: "center" }}
          >
            <EyeInvisibleOutlined style={{ fontSize: 18 }} />
            <span>{t("reports.canvas.widgetUnavailable")}</span>
          </Flex>
        </Card>
      ) : state === "error" ? (
        <Card
          size="small"
          title={title}
          style={placeholderCardStyle}
          styles={{ body: placeholderBodyStyle }}
        >
          <Flex vertical align="center" gap={8} data-testid="widget-error">
            <span style={{ color: neutral.muted, textAlign: "center" }}>
              {t("reports.canvas.widgetError")}
            </span>
            <Button size="small" onClick={onRefresh}>
              {t("reports.canvas.retry")}
            </Button>
          </Flex>
        </Card>
      ) : widget.type === "chart" ? (
        <Card
          size="small"
          title={title}
          style={chartCardStyle}
          styles={{ body: chartCardBodyStyle }}
        >
          {state === "loading" ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : (
            <WidgetRenderer
              widget={widget}
              payload={payload}
              state={state}
              currency={currency}
              fillHeight
              onEngagementDrill={onEngagementDrill}
              loadEngagementRange={loadEngagementRange}
            />
          )}
        </Card>
      ) : (
        <div
          className="reports-tile-frame"
          data-testid="tile-frame"
          style={frameWrapperStyle}
        >
          <WidgetRenderer
            widget={widget}
            payload={payload}
            state={state}
            currency={currency}
            onRefresh={onRefresh}
            onExpand={onExpand}
            onEngagementDrill={onEngagementDrill}
            loadEngagementRange={loadEngagementRange}
          />
        </div>
      )}
    </div>
  )
}
